import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { buildUrlWithCurrentParams } from '@/scopes/checkout/domain/helpers/BuildUrlWithCurrentParams'
import { useBookingDraftQuery, useCheckoutSid, useUnselectedServices } from '@/scopes/checkout/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useDecision } from '@optimizely/react-sdk'
import { Button, Modal, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { Icon } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useState } from 'react'
import { useSessionStorage } from 'react-use'
import { styled } from 'styled-components'
import { createAncillariesClickedEvent } from '../domain/analytics'
import { ServiceIcon } from './ServiceIcon'
import { getServiceName } from './ServiceName'

const Wrapper = styled.div`
  max-width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: ${tk('spacing_5xl')};

  @media ${tk('mq_desktop_md')} {
    margin-bottom: ${tk('spacing_none')};
  }
`

const NextStepButton = styled(Button)`
  margin: 0 auto;
`
const StyledServiceLine = styled(SpaceBetween)`
  width: 100%;
`

export function CheckoutNextStep() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isLoading, data } = useBookingDraftQuery()
  const { unselectedServiceTypes } = useUnselectedServices()
  const intl = useIntl()
  const sid = useCheckoutSid()
  const { sendEvent } = useAnalytics()
  const [step2InReactDecision] = useDecision('id_405_checkout_2_react_wg')

  const [optionalAncillariesModalIsShown, setOptionalAncillariesModalIsShown] = useSessionStorage(
    `${sid}-next-step-optional-ancillaries-modal-is-shown`,
    false,
  )

  const nextStep = data?.navigation?.nextStep
  if (!nextStep)
    return null

  const onClose = () => {
    setIsModalOpen(false)
    sendEvent(
      createAncillariesClickedEvent({
        result: 'back to ancillaries',
        description: 'optional ancillaries popup',
        element: 'element clicked',
        interaction: true,
      }),
    )
  }

  const goToNextStep = () => {
    let nextStepUrl = nextStep.url
    if (isModalOpen) {
      onClose()
    }
    // TODO: Remove after AB Test id_405_checkout_2_react_wg
    if (step2InReactDecision.enabled) {
      nextStepUrl = nextStepUrl.replace('/wintersport/bookingpage/', '/wintersport/bookingpages/')
    }

    window.location.href = buildUrlWithCurrentParams(nextStepUrl)
  }

  const openModal = () => {
    if (optionalAncillariesModalIsShown || !unselectedServiceTypes.length) {
      goToNextStep()
      return
    }
    if (!optionalAncillariesModalIsShown) {
      setIsModalOpen(true)
      setOptionalAncillariesModalIsShown(true)
      sendEvent(
        createAncillariesClickedEvent({
          result: 'pop-up shown',
          description: 'optional ancillaries popup',
          element: 'element visible',
          interaction: false,
        }),
      )
    }
  }

  return (
    <Wrapper>
      <NextStepButton
        $hierarchy="primary"
        $size="default"
        onClick={openModal}
        disabled={isLoading}
        data-qa="checkout-next-step-button"
      >
        <Typography $variant="label">{nextStep.text}</Typography>
        <Icon size={16} glyph="ChevronRight" />
      </NextStepButton>

      <Modal.Root onClose={onClose} open={isModalOpen}>
        <Modal.Content $size="md">
          <Modal.Header>
            <Modal.HeaderTitle text={intl.formatMessage({ id: 'checkout_steps_next_step_modal_title' })} />
            <Modal.HeaderCloseButton />
          </Modal.Header>
          <Modal.Body>
            <SpaceBetween alignItems="center" direction="y" padding={tk('spacing_md')} spacing={tk('spacing_sm')}>
              <Typography $variant="body-md" as="p">
                {intl.formatMessage({ id: 'checkout_steps_next_step_modal_description' })}
              </Typography>
              {unselectedServiceTypes.map(id => (
                <ServiceLine key={id} type={id} />
              ))}
            </SpaceBetween>
          </Modal.Body>
          <Modal.Footer>
            <Button $hierarchy="tertiary" $withoutColor onClick={onClose}>
              {intl.formatMessage({ id: 'checkout_steps_next_step_modal_cancel_button_text' })}
            </Button>
            <Button $hierarchy="primary" onClick={goToNextStep} data-qa="checkout-next-step-continue-button">
              {intl.formatMessage({ id: 'checkout_steps_next_step_modal_continue_button_text' })}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </Wrapper>
  )
}

function ServiceLine({ type }: { type: string }) {
  const items = useCMSPageField('Checkout.items')?.items ?? []
  const checkoutItem = items.find(item => item.aName?.includes(type))
  const intl = useIntl()

  if (!type || !checkoutItem)
    return null
  const serviceName = getServiceName(type, intl)

  return (
    <StyledServiceLine direction="x" spacing={tk('spacing_sm')} alignItems="center">
      <ServiceIcon type={type} size={16} />
      <Typography $variant="body-md">
        {serviceName || checkoutItem?.optionalAncillariesPopupUnselectedServiceText || checkoutItem?.componentTitle}
      </Typography>
    </StyledServiceLine>
  )
}
